/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type {
	DashboardConfigAndWRMResourceData,
	DashboardConfigType,
} from '@jira/global-pages__dashboard-common/src/types.tsx';
// eslint-disable-next-line jira/import-whitelist
import { mapToReactGadgets } from '@jira/global-pages__dashboard-common/src/utils/map-to-react-gadgets.tsx';
import type { RouterDataContext } from '@atlassian/react-resource-router';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import getDashboardConfig, { getDashboardsConfig } from './getDashboardConfig.tsx';
import fetchWrmBundleDescriptor from './fetchWrmBundleDescriptor.tsx';
import generatePreloadLinkTags from './generatePreloadLinkTags.tsx';
import generateScriptTags from './generateScriptTags.tsx';
import { getKey } from './getKey.tsx';
import { gadgetsToUniqueSortedAmdModules } from './gadgetsToUniqueSortedAmdModules.tsx';

const generateScriptTagAndPreloadedModuleNames = async (
	configs: DashboardConfigType[],
): Promise<DashboardConfigAndWRMResourceData[]> => {
	const gadgets = configs.flatMap((config) => config.gadgets);
	const preloadedModuleNames = gadgetsToUniqueSortedAmdModules(gadgets);
	const wrmBundleDescriptor = await fetchWrmBundleDescriptor(preloadedModuleNames);
	return configs.map((config) => {
		const scriptTags =
			[
				wrmBundleDescriptor ? generateScriptTags(wrmBundleDescriptor) : null,
				generatePreloadLinkTags(config),
			]
				.filter(Boolean)
				.join('\n') || null;

		return { ...config, scriptTags, preloadedModuleNames };
	});
};

const getConfigAndWRMData = async (
	context: RouterDataContext,
): Promise<DashboardConfigAndWRMResourceData[]> => {
	// this is either for single dashboard/wallboard or wallboard slide show with multiple dashboards
	try {
		const { route, query } = context;
		const originalConfigs: DashboardConfigType[] =
			route.name === 'dashboard-wallboard'
				? await getDashboardsConfig(query.dashboardId)
				: [await getDashboardConfig(getKey(context))];

		const configs = originalConfigs.map((config) => ({
			...config,
			gadgets: mapToReactGadgets(config.gadgets),
		}));

		if (!__SERVER__) {
			return configs.map((config) => ({ ...config, preloadedModuleNames: [] }));
		}

		return generateScriptTagAndPreloadedModuleNames(configs);
	} catch (error: unknown) {
		logger.unsafeErrorWithCustomerData(
			'spa-apps.dashboard.get-config-and-wrm-data',
			`Failed to get dashboard configuration and wrm data: route=[${context.route.name}], query=[${JSON.stringify(context.query)}]`,
			error instanceof Error ? error : undefined,
		);
		throw error;
	}
};

export default getConfigAndWRMData;
